import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Column, Row } from "react-table";
import ReactTooltip from "react-tooltip";

import style from "./tenants.scss";
import CopyToClipboard from "components/copy-to-clipboard/CopyToClipboard";
import { ViewTenantDetails } from "components/customer-details/ViewTenantDetails";
import Chevron from "components/icons/Chevron";
import KebabMenu from "components/kebab-menu/KebabMenu";
import { subpageContext } from "components/layout/ApplicationLayout";
import { SubpageLayout } from "components/layout/subpage-layout/SubpageLayout";
import { allowDelivery } from "components/licenses/delivery-history/AddLicenseDeliveryView";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import SearchView from "components/search/SearchView";
import { SkeletonLoader } from "components/skeleton-loader/SkeletonLoader";
import StatusBadge, { Status } from "components/status-badge/StatusBadge";
import DateCell from "components/table/DateCell";
import Table, { deriveColumnWidth } from "components/table/Table";
import tableStyle from "components/table/table.scss";
import TextWithTooltip from "components/table/TextWithTooltip";
import TenantMenuItems from "components/tenants/TenantMenuItems";
import { AUTH_TENANT_ACCESS, AUTH_TENANT_CREATE, AUTH_TENANT_EDIT, AUTH_TENANT_VIEW } from "domain/authority";
import {
    calculateTrialDaysLeft,
    CombinedTier,
    contractTypeToTranslationMap,
    countDaysTillDeletion,
    LicensingModel,
    showDaysTillDeletion,
    Tenant,
    TenantCursor,
    Tenants,
    TenantType,
} from "domain/tenants";
import { generateTenantTypeToTranslationMap, generateTierToTranslationMap } from "domain/tierRelatedMaps";
import { ProductToRateList } from "services/licenses/LicenseService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { tenantService } from "services/tenants/TenantService";
import { userSessionService } from "services/user/UserSessionService";
import { store } from "store";
import buttonStyle from "styles/buttons.scss";
import formStyle from "styles/form.scss";
import layoutStyle from "styles/layout.scss";
import { RepositoryKey } from "utils/repository";

import testIds from "testIds.json";

interface TableState {
    tenants: Tenant[];
    cursor: TenantCursor | null;
    scrollPosition?: number;
}
export function TenantsTable(rawProps: {
    scrollPosition?: number;
    count: number;
    refreshCount: number;
    onTenantEdit: () => void;
    onTenantDelete: () => void;
    initialTenantData?: Tenants;
    parentLevel: number;
    setNewComponent: (value: boolean) => void;
    tenantUuid: (value: string) => void;
    tenantName: (value: string) => void;
    rateVersions?: ProductToRateList[];
}): JSX.Element {
    const props = { initialTenantData: { tenantList: [], cursor: null }, ...rawProps };
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState<boolean>(false);
    const tableContainerRef = React.useRef<HTMLDivElement>(null);
    const rowRefs = React.useRef<Array<React.RefObject<HTMLTableRowElement>>>([]);
    const [search, setSearchQuery] = React.useState("");
    const [selectedSearchParameter, setSelectedSearchParameter] = React.useState("search");
    const [selectedContractTypeFilter, setSelectedContractTypeFilter] = React.useState("all");
    const [state, setState] = React.useState<TableState>({
        tenants: props.initialTenantData.tenantList,
        cursor: props.initialTenantData.cursor,
        scrollPosition: props.scrollPosition,
    });
    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string>("");
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [initialLoading, setInitialLoading] = React.useState<boolean>(false);
    const context = React.useContext(subpageContext);
    const fetchTenantInfoAllowed =
        userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
        userSessionService.userHasAllAuthorities([AUTH_TENANT_ACCESS]);
    const showTenantProfile = (tenant: Tenant) => {
        context?.setSubpage({
            content: (
                <SubpageLayout
                    visible={true}
                    title={t("CustomerProfile.customerName", { customerName: tenant.name })}
                    primaryButtonRequired={true}
                >
                    {<ViewTenantDetails activeTenant={tenant} />}
                </SubpageLayout>
            ),
            isModal: true,
        });
    };

    const createDropdownArrow = (isExpanded: boolean) => {
        const theme = store.getState();

        return (
            <Chevron
                color={theme.themeReducer.theme.iconFillColor}
                className={classNames(style.toggleArrow, style.right, { [style.down]: isExpanded })}
                width={20}
                height={20}
            />
        );
    };

    const showConnectorLines = (depth: number) => {
        return (
            <div className={tableStyle.nameCellConnectorLines}>
                <span></span>
                {depth >= 2 && <span></span>}
                {depth >= 3 && <span></span>}
            </div>
        );
    };

    const highlightText = (text: string, highlight: string) => {
        const escapedHighlight = highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const partToHighlight = new RegExp(`(${escapedHighlight})`, "i");
        const fullText = text.split(partToHighlight);

        return (
            <div>
                {fullText.map((word, index) =>
                    word.toLowerCase() === highlight.toLowerCase() ? <mark key={index}>{word}</mark> : word
                )}
            </div>
        );
    };

    const addSubTenants = (tenantList: Tenant[], data: Tenant[], row: Row<Tenant>): Tenant[] => {
        return addSubTenantsToTenants(tenantList, data, row, row.id.split("."));
    };

    const addSubTenantsToTenants = (
        tenantList: Tenant[],
        data: Tenant[],
        row: Row<Tenant>,
        rowId: string[]
    ): Tenant[] => {
        const tenantId = Number(rowId[0]);

        if (rowId.length === 1) {
            return tenantList.map((tenant, index) => {
                if (index !== tenantId) {
                    return tenant;
                }

                return {
                    ...tenant,
                    tenants: data,
                };
            });
        }

        return tenantList.map((tenant, index) => {
            if (index !== tenantId) {
                return tenant;
            }

            if (!tenant.tenants) {
                return tenant;
            }

            const pathToSubTenant = rowId.slice(1);

            return {
                ...tenant,
                tenants: addSubTenantsToTenants(tenant.tenants, data, row, pathToSubTenant),
            };
        });
    };

    const createPlaceholderTableRows = (row: Row<Tenant>) => {
        const placeholderObjects = Array(row.original.subTenantsCount).fill({});

        setState((prevState) => ({
            ...prevState,
            tenants: addSubTenants(prevState.tenants, placeholderObjects, row),
        }));
    };

    const fetchSubTenants = (row: Row<Tenant>) => {
        const abortController = new AbortController();
        abortControllers.push(abortController);

        tenantService
            .fetchSubTenants(row.original.uuid, abortController)
            .then((data) => {
                const currentDepth = row.original.depth ?? 1;
                const tenantsWithDepth = addDepthToTenants(data.tenantList.tenantList, currentDepth + 1);
                setState((prevState) => ({
                    ...prevState,
                    tenants: addSubTenants(prevState.tenants, tenantsWithDepth, row),
                }));
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setRequestFailureMessage(t("CustomersTable.requestFailed"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setInitialLoading(false);
                }
            });
    };

    const createSelectedTenantHierarchy = (rows: Row<Tenant>[], selectedRowId: string): Row<Tenant>[] => {
        const computedRows: Row<Tenant>[] = [];
        const selectedRowDepth = selectedRowId.split(".");
        let reconstructedId = "";
        for (const idPart of selectedRowDepth) {
            if (reconstructedId.length === 0) {
                reconstructedId = idPart;
            } else {
                reconstructedId = reconstructedId + "." + idPart;
                if (reconstructedId === selectedRowId) {
                    break;
                }
            }
            for (const eachRow of rows) {
                if (reconstructedId === eachRow.id) {
                    computedRows.push(eachRow);
                    break;
                }
            }
        }
        return computedRows;
    };

    function showDelivery(
        licensingModel: LicensingModel,
        parentModel: LicensingModel,
        parentType: TenantType,
        parentTier: CombinedTier,
        tenantTier: CombinedTier
    ) {
        return allowDelivery(parentType, parentModel, licensingModel, parentTier, tenantTier);
    }

    function showCommonLicenseCount(licensingModel: string, commonLicenseCount?: number) {
        if (licensingModel !== LicensingModel.BUNDLE_WITH_TOKEN || typeof commonLicenseCount === "undefined") {
            return t("Common.na");
        }
        return commonLicenseCount;
    }

    const columns: Array<Column<Tenant>> = [
        {
            Header: () => <TextWithTooltip text={t("Common.customerName")} key="1" />,
            className: style.kebabMenuOpen,
            accessor: "name",
            fixedToLeft: true,
            Cell: ({ rows, row, value, isLoading }) => {
                const isSubRow = row.depth >= 1;
                const rowRef = React.useRef<HTMLTableRowElement>(null);
                const menuRef = React.useRef<HTMLDivElement>(null);
                const [showAbove, setShowAbove] = React.useState(false);
                rowRefs.current.push(rowRef);

                const handleKebabClick = () => {
                    if (menuRef.current) {
                        const rect = menuRef.current.getBoundingClientRect();
                        setTimeout(() => {
                            const backupMenuHeight = 340;
                            const menuHeight =
                                (document.querySelector(".src-components-tenants-tenants__kebabMenu") as HTMLElement)
                                    ?.offsetHeight || backupMenuHeight;
                            const tableBottom =
                                tableContainerRef.current?.getBoundingClientRect().bottom || window.innerHeight;
                            const tableTop = tableContainerRef.current?.getBoundingClientRect().top || 0;
                            const headerHeight =
                                document.querySelector('[role="columnheader"]')?.getBoundingClientRect().height || 0;
                            setShowAbove(
                                rect.bottom + menuHeight > tableBottom &&
                                    rect.top - menuHeight >= tableTop + headerHeight
                            );
                        }, 0);
                    }
                };
                return (
                    <div
                        ref={rowRef}
                        className={classNames(style.alignment, {
                            [tableStyle.subRowBorder]: isSubRow || (row.depth === 0 && row.isExpanded),
                            [tableStyle.subRowNameCell]: isSubRow,
                            [tableStyle.expandedRow]: row.isExpanded,
                            [style.disabledContainer]: row.original.deletionStatus,
                        })}
                        data-depth={row.depth}
                    >
                        {isSubRow && showConnectorLines(row.depth)}
                        {isLoading ? (
                            <div className={style.nameCellLoader}>
                                <SkeletonLoader width={15} />
                                <SkeletonLoader />
                            </div>
                        ) : (
                            <>
                                <div className={style.kebabIcon} ref={menuRef} onClick={handleKebabClick}>
                                    {userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                                        userSessionService.userHasAnyAuthority([
                                            AUTH_TENANT_ACCESS,
                                            AUTH_TENANT_EDIT,
                                            AUTH_TENANT_VIEW,
                                            AUTH_TENANT_CREATE,
                                        ]) && (
                                            <KebabMenu>
                                                <TenantMenuItems
                                                    uuid={row.original.uuid}
                                                    customerName={value}
                                                    status={row.original.status}
                                                    onTenantEdit={props.onTenantEdit}
                                                    onTenantDelete={props.onTenantDelete}
                                                    tier={row.original.tier}
                                                    creationDate={row.original.createdTimestamp}
                                                    tenantRegion={row.original.region}
                                                    expirationDate={row.original.expirationDate}
                                                    countryCode={row.original.countryCode}
                                                    contactName={row.original.contactName}
                                                    contactEmail={row.original.contactEmail}
                                                    salesforceLeadId={row.original.salesforceLeadId}
                                                    notes={row.original.notes}
                                                    salesforceAccountId={row.original.salesforceAccountId}
                                                    parentExpirationDate={row.original.parentExpirationDate}
                                                    licenses={row.original.productsLicensed}
                                                    licenseCount={
                                                        row.original.availableLicenses == undefined
                                                            ? 0
                                                            : row.original.availableLicenses
                                                    }
                                                    userCount={
                                                        row.original.totalUsers == undefined
                                                            ? 0
                                                            : row.original.totalUsers
                                                    }
                                                    workflowCount={
                                                        row.original.totalWorkflows == undefined
                                                            ? 0
                                                            : row.original.totalWorkflows
                                                    }
                                                    reportCount={
                                                        row.original.totalReportsCount == undefined
                                                            ? 0
                                                            : row.original.totalReportsCount
                                                    }
                                                    parentLevel={props.parentLevel - 1}
                                                    showMenuAbove={showAbove}
                                                    tenantType={row.original.type}
                                                    rowsUntilSubTenant={
                                                        isSubRow ? createSelectedTenantHierarchy(rows, row.id) : []
                                                    }
                                                    setNewComponent={props.setNewComponent}
                                                    setTenantUuid={props.tenantUuid}
                                                    setTenantName={props.tenantName}
                                                    licensingModel={row.original.licensingModel}
                                                    rateUuid={row.original.rateUuid}
                                                    deliveryAllowed={showDelivery(
                                                        row.original.licensingModel,
                                                        row.original.parentLicensingModel,
                                                        row.original.parentType,
                                                        row.original.parentTier,
                                                        row.original.tier
                                                    )}
                                                    parentLicensingModel={row.original.parentLicensingModel}
                                                    rateVersions={props.rateVersions}
                                                    parentType={row.original.parentType}
                                                    parentTier={row.original.parentTier}
                                                    salesforceContractId={row.original.salesforceContractId}
                                                    salesforceContractState={row.original.salesforceContractState}
                                                    salesforceContactEmail={row.original.salesforceContactEmail}
                                                    salesforceContractEndDate={row.original.salesforceContractEndDate}
                                                    parentDrawBackPrevention={row.original.parentDrawbackPrevention}
                                                    drawbackPrevention={row.original.drawbackPrevention}
                                                    contractType={row.original.contractType}
                                                    parentContractType={
                                                        row.original.parentType === "INTERNAL"
                                                            ? row.original.contractType
                                                            : row.original.parentContractType
                                                    }
                                                    automaticDeletion={row.original.automaticDeletion ?? false}
                                                    diskQuotaMib={row.original.diskQuotaMib}
                                                    uploadLimitMib={row.original.uploadLimitMib}
                                                    downloadLimitMib={row.original.downloadLimitMib}
                                                    depth={row.original.depth ?? 1}
                                                    rowTenantLevel={props.parentLevel + row.depth}
                                                    trialDaysLeft={calculateTrialDaysLeft(row.original)}
                                                    tenants={state.tenants
                                                        .filter((tenant) => tenant.parentUuid !== undefined)
                                                        .map((tenant) => ({
                                                            uuid: tenant.uuid,
                                                            name: tenant.name,
                                                            parentUuid: tenant.parentUuid!,
                                                            tier: tenant.tier,
                                                            licensingModel: tenant.licensingModel,
                                                            type: tenant.type,
                                                        }))}
                                                />
                                            </KebabMenu>
                                        )}
                                </div>

                                {row.original.subTenantsCount > 0 && fetchTenantInfoAllowed && (
                                    <button
                                        onClick={() => {
                                            if (row.subRows.length === 0) {
                                                createPlaceholderTableRows(row);
                                                fetchSubTenants(row);
                                            }
                                            row.toggleRowExpanded();
                                        }}
                                        className={style.subTenantToggleButton}
                                        data-testid={testIds.common.primaryView.table.expandArrow}
                                        aria-expanded={row.isExpanded}
                                    >
                                        <TextWithTooltip
                                            text={
                                                row.isExpanded
                                                    ? t("CustomersTable.tenantExpandedDropdownLabel", {
                                                          shownCount: row.subRows.length,
                                                          totalCount: row.original.subTenantsCount,
                                                          tenantName: value,
                                                      })
                                                    : t("CustomersTable.tenantCollapsedDropdownLabel", {
                                                          tenantName: value,
                                                          totalCount: row.original.subTenantsCount,
                                                      })
                                            }
                                        >
                                            {createDropdownArrow(row.isExpanded)}
                                        </TextWithTooltip>
                                    </button>
                                )}

                                <div className={classNames(style.customerNameCell, style.nameWithDropdown)}>
                                    <button
                                        onClick={() => {
                                            showTenantProfile(row.original);
                                            usageStatisticsService.sendEvent({
                                                category: Category.TENANT,
                                                action: Action.VIEW_TENANT_DETAILS,
                                            });
                                        }}
                                        className={buttonStyle.textButton}
                                    >
                                        <TextWithTooltip text={value} />
                                    </button>

                                    {row.original.foundUser && (
                                        <div className={style.userInfo}>
                                            {row.original.foundUserName && (
                                                <>
                                                    {highlightText(row.original.foundUserName, search)}
                                                    <span className={style.userInfoSeparator}>•</span>
                                                </>
                                            )}
                                            {highlightText(row.original.foundUser, search)}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                );
            },
            width: deriveColumnWidth(24, tableContainerRef),
        },
        {
            Header: () => <TextWithTooltip text={t("CustomersTable.usecaseType")} key="11" />,
            accessor: "contractType",
            Cell: ({ row, cell: { value } }) => {
                const tenant = row.original;
                const trialDaysLeft = calculateTrialDaysLeft(tenant);
                const contractType = value ?? "PRODUCTION";

                let trialStatusBadge = null;

                if (contractType === "TRIAL") {
                    if (trialDaysLeft !== null && trialDaysLeft > 0) {
                        trialStatusBadge = (
                            <StatusBadge
                                values={[
                                    {
                                        status: Status.WARNING,
                                        title: t("CustomerProfile.trialTenantsNavPanel.trialDaysLeft", {
                                            days: trialDaysLeft,
                                        }),
                                    },
                                ]}
                            />
                        );
                    } else {
                        trialStatusBadge = (
                            <StatusBadge
                                values={[
                                    {
                                        status: Status.NEUTRAL,
                                        title: t("CustomerProfile.trialTenantsNavPanel.trialEndedText"),
                                    },
                                ]}
                            />
                        );
                    }
                }
                return (
                    <div
                        className={classNames(style.alignment, {
                            [style.disabledContainer]: row.original.deletionStatus,
                        })}
                    >
                        <TextWithTooltip text={contractTypeToTranslationMap(t).get(value)}>
                            {trialStatusBadge}
                        </TextWithTooltip>
                    </div>
                );
            },
            width: deriveColumnWidth(14, tableContainerRef),
        },
        {
            Header: () => <TextWithTooltip text={t("CustomersTable.bundle")} key="9" />,
            accessor: "tier",
            Cell: (cellInfo) => {
                return (
                    <div
                        className={classNames(style.alignment, {
                            [style.disabledContainer]: cellInfo.cell.row.original.deletionStatus,
                        })}
                    >
                        {cellInfo.isLoading ? (
                            <SkeletonLoader />
                        ) : (
                            <TextWithTooltip
                                text={
                                    generateTenantTypeToTranslationMap(t).get(cellInfo.row.original.type) +
                                    " - " +
                                    generateTierToTranslationMap(t).get(cellInfo.value)
                                }
                            />
                        )}
                    </div>
                );
            },
            width: deriveColumnWidth(14, tableContainerRef),
        },
        {
            Header: () => <TextWithTooltip text={t("CustomersTable.commonLicenseCount")} key="10" />,
            accessor: "commonLicenseCount",
            Cell: (cellInfo) => {
                return (
                    <div
                        className={classNames(style.alignment, {
                            [style.disabledContainer]: cellInfo.cell.row.original.deletionStatus,
                        })}
                    >
                        {cellInfo.isLoading ? (
                            <SkeletonLoader />
                        ) : (
                            <TextWithTooltip
                                text={showCommonLicenseCount(
                                    cellInfo.cell.row.original.licensingModel,
                                    cellInfo.cell.value
                                )}
                            />
                        )}
                    </div>
                );
            },
            width: deriveColumnWidth(14, tableContainerRef),
        },

        {
            Header: () => <TextWithTooltip text={t("Common.status")} key="2" />,
            accessor: "status",
            Cell: (cellInfo) => {
                return (
                    <div
                        className={classNames(style.alignment, {
                            [style.disabledContainer]: cellInfo.cell.row.original.deletionStatus,
                        })}
                    >
                        {cellInfo.isLoading ? (
                            <SkeletonLoader />
                        ) : (
                            <StatusBadge
                                values={[
                                    {
                                        status: cellInfo.value
                                            ? Status.SUCCESS
                                            : !showDaysTillDeletion(
                                                    cellInfo.row.original.contractType,
                                                    cellInfo.row.original.automaticDeletion
                                                )
                                              ? Status.NEUTRAL
                                              : Status.ERROR,
                                        title: cellInfo.value
                                            ? t("Common.active")
                                            : !showDaysTillDeletion(
                                                    cellInfo.row.original.contractType,
                                                    cellInfo.row.original.automaticDeletion
                                                )
                                              ? t("Common.inactive")
                                              : t("CustomersTable.daysTillDeletion", {
                                                    daysTillDeletion: countDaysTillDeletion(
                                                        cellInfo.cell.row.original.expirationDate
                                                    ),
                                                }),
                                    },
                                ]}
                                tooltip={true}
                            />
                        )}
                    </div>
                );
            },
            width: deriveColumnWidth(8, tableContainerRef),
        },
        {
            Header: () => <TextWithTooltip text={t("CustomersTable.contactEmail")} key="5" />,
            accessor: "contactEmail",
            Cell: (cellInfo) => {
                return (
                    <div
                        className={classNames(style.alignment, {
                            [style.disabledContainer]: cellInfo.cell.row.original.deletionStatus,
                        })}
                    >
                        {cellInfo.isLoading ? <SkeletonLoader /> : <TextWithTooltip text={cellInfo.value} />}
                        {cellInfo.value && <CopyToClipboard value={cellInfo.value} />}
                    </div>
                );
            },
            width: deriveColumnWidth(14, tableContainerRef),
        },
        {
            Header: () => <TextWithTooltip text={t("CustomersTable.emsAccountId")} key="6" />,
            accessor: "emsId",
            Cell: (cellInfo) => {
                return (
                    <div
                        className={classNames(style.alignment, {
                            [style.disabledContainer]: cellInfo.cell.row.original.deletionStatus,
                        })}
                    >
                        {cellInfo.isLoading ? <SkeletonLoader /> : <TextWithTooltip text={cellInfo.value} />}
                    </div>
                );
            },
            width: deriveColumnWidth(16, tableContainerRef),
        },
        {
            Header: () => <TextWithTooltip text={t("CustomersTable.accountId")} key="7" />,
            accessor: "salesforceAccountId",
            Cell: (cellInfo) => {
                return (
                    <div
                        className={classNames(style.alignment, {
                            [style.disabledContainer]: cellInfo.cell.row.original.deletionStatus,
                        })}
                    >
                        {cellInfo.isLoading ? <SkeletonLoader /> : <TextWithTooltip text={cellInfo.value} />}
                    </div>
                );
            },
            width: deriveColumnWidth(16, tableContainerRef),
        },
        {
            Header: () => <TextWithTooltip text={t("CustomersTable.expirationDate")} key="7" />,
            accessor: "expirationDate",
            Cell: (cellInfo) => {
                let expirationDate;

                if (!cellInfo.isLoading) {
                    cellInfo.value
                        ? (expirationDate = <DateCell tooltip={true} value={cellInfo.value} withoutTime={true} />)
                        : (expirationDate = <TextWithTooltip text={t("Common.never")} />);
                }
                return (
                    <div
                        className={classNames(style.alignment, {
                            [style.disabledContainer]: cellInfo.cell.row.original.deletionStatus,
                        })}
                    >
                        {cellInfo.isLoading ? <SkeletonLoader /> : expirationDate}
                    </div>
                );
            },

            width: deriveColumnWidth(14, tableContainerRef),
        },
        {
            Header: () => <TextWithTooltip text={t("Common.notes")} key="8" />,
            accessor: "notes",
            Cell: (cellInfo) => {
                return (
                    <div
                        className={classNames(style.alignment, {
                            [style.disabledContainer]: cellInfo.cell.row.original.deletionStatus,
                        })}
                    >
                        {cellInfo.isLoading ? <SkeletonLoader /> : <TextWithTooltip text={cellInfo.value} />}
                    </div>
                );
            },
            width: deriveColumnWidth(14, tableContainerRef),
        },
    ];

    const fetchData = (initialLoading: boolean) => {
        setLoading(true);
        setInitialLoading(initialLoading);
        const abortController = new AbortController();
        abortControllers.push(abortController);

        tenantService
            .fetchTenants(
                abortController,
                initialLoading ? null : state.cursor,
                selectedSearchParameter === "search" ? search : "",
                selectedSearchParameter === "username" ? search : "",
                undefined,
                undefined,
                selectedContractTypeFilter
            )
            .then((data) => {
                setState((prevState) => ({
                    ...prevState,
                    tenants: prevState.tenants.concat(data.tenantList.tenantList),
                    scrollPosition: prevState.tenants.length - 1,
                    cursor: data.tenantList.cursor,
                }));
                setLoading(false);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setRequestFailureMessage(t("CustomersTable.requestFailed"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setInitialLoading(false);
                }
            });
    };

    React.useEffect(() => {
        setState({
            tenants: [],
            cursor: null,
            scrollPosition: 0,
        });
        if (props.initialTenantData.tenantList.length > 0) {
            return;
        }

        fetchData(true);
        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, [props.count, search, props.refreshCount, selectedContractTypeFilter]);

    React.useEffect(() => {
        ReactTooltip.rebuild();
    }, [state.tenants]);

    let dataCount = null;
    if (state.tenants.length > 0) {
        dataCount = t("Common.defaultSearchResultHint", { dataCount: state.tenants.length });
    }

    const emptyStateMessage = search
        ? t("Common.searchEmptyStateMessage", { searchQuery: search })
        : t("CustomersTable.emptyStateMessage");

    function generateContractTypeOptions(): string[] {
        return ["all", "trial", "production"];
    }

    const contractTypeToTranslationKeyMap = new Map<string, string>([
        ["all", t("CustomersTable.allUsecaseType")],
        ["trial", t("AddCustomerForm.contractType.trial")],
        ["production", t("AddCustomerForm.contractType.production")],
    ]);

    return (
        <>
            <div className={layoutStyle.aboveTable}>
                <div className={layoutStyle.recordCount}>{dataCount}</div>

                <div className={style.filter}>
                    {
                        <>
                            <div className={classNames(formStyle.search, style.marginRight16Px)}>
                                <select
                                    id={"contractTypeSelector"}
                                    value={selectedContractTypeFilter}
                                    onChange={(event) => setSelectedContractTypeFilter(event.target.value)}
                                    className={classNames(formStyle.select, formStyle.fixedWidthInput)}
                                    data-testid={testIds.workArea.tenant.usecaseTypeSelect}
                                >
                                    {generateContractTypeOptions().map((value, index) => (
                                        <option key={index} value={value}>
                                            {contractTypeToTranslationKeyMap.get(value)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className={classNames(formStyle.search, style.marginRight4Px)}>
                                <SearchView
                                    key="tenantDetails"
                                    setSearch={setSearchQuery}
                                    searchInProgress={loading}
                                    placeholder={t("Common.searchBy")}
                                />
                            </div>
                            <div className={formStyle.search}>
                                <select
                                    id={"searchParameterSelector"}
                                    value={selectedSearchParameter}
                                    onChange={(event) => setSelectedSearchParameter(event.target.value)}
                                    className={classNames(formStyle.select, formStyle.fixedWidthInput)}
                                    data-testid={testIds.workArea.tenant.searchSelect}
                                >
                                    <option key={"tenantsDetails"} value={"search"}>
                                        {t("CustomersTable.tenantDetails")}
                                    </option>
                                    <option key={"users"} value={"username"}>
                                        {t("Common.users")}
                                    </option>
                                </select>
                            </div>
                        </>
                    }
                </div>
            </div>
            <div className={layoutStyle.tableWrapper} ref={tableContainerRef}>
                <Table
                    tableIdentity={RepositoryKey.TENANT_TABLE}
                    data={state.tenants.sort((a, b) => a.name.localeCompare(b.name))}
                    columns={columns}
                    loaded={!initialLoading}
                    failureMessage={requestFailureMessage}
                    tooltips={true}
                    emptyMessage={emptyStateMessage}
                    testId={testIds.workArea.tenant.table.itself}
                    noAction={search !== ""}
                />
            </div>
            {state.cursor != null &&
                state.tenants.length >= 100 &&
                state.tenants.length != 0 &&
                requestFailureMessage === "" &&
                (loading ? (
                    <LoadingIndicator small={true} withTable />
                ) : (
                    <button
                        className={buttonStyle.loadMoreButtonWithoutIcon}
                        onClick={() => {
                            fetchData(false);
                        }}
                        data-testid={testIds.common.primaryView.table.loadMoreButton}
                    >
                        {t("Common.loadMore")}
                    </button>
                ))}
        </>
    );
}

function addDepthToTenants(tenants: Tenant[], depth: number): Tenant[] {
    return tenants.map((tenant) => ({
        ...tenant,
        depth,
    }));
}
